<template>
  <div class="container">
    <div class="d-flex flex-column justify-content-center align-items-center" :style="isAuth0 ? 'min-height: 80vh;' : ''">

      <Brand :width="120" :height="120" :withChat="false" />

      <!-- Auth0 mode: error state -->
      <template v-if="isAuth0">
        <div v-if="error" class="card col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4 col-xxl-3 rounded-custom shadow border-0 p-3">
          <div class="card-body text-center">
            <div class="alert alert-danger" role="alert">
              <small><strong class="me-1">Errore:</strong>{{ error }}</small>
            </div>
            <button class="btn btn-violet" @click="redirectToAuth0">
              Riprova
            </button>
          </div>
        </div>

        <div v-else class="d-flex flex-column align-items-center mt-4">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <small class="mt-2 text-muted">Autenticazione in corso...</small>
        </div>
      </template>

      <!-- Legacy mode: email/password form -->
      <template v-else>
        <div class="card col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4 col-xxl-3 rounded-custom shadow border-0 p-3">
          <div class="card-body">

            <form @submit.prevent="submit" class="row g-3 needs-validation" novalidate>
              <h2 class="mb-3 fw-normal text-center">Login</h2>

              <div class="mb-3">
                <label for="email" class="form-label">{{ $t('Email address') }}</label>
                <input v-model="data.email" type="email"
                  :class="errors.length > 0 ? 'form-control is-invalid' : 'form-control'" placeholder="name@primolab.eu"
                  required />
              </div>

              <div class="mb-3">
                <label for="password" class="form-label">Password</label>
                <input v-model="data.password" type="password"
                  :class="errors.length > 0 ? 'form-control is-invalid' : 'form-control'" required />
              </div>

              <div class="mb-3">
                <router-link to="/recover-password" href="#" class="link-dark">
                  {{ $t('Reset password') }}
                </router-link>
              </div>

              <template v-if="errors.length > 0">
                <template v-for="(error, index) in errors" :key="index">
                  <div class="alert alert-danger m-0 d-flex align-items-center" role="alert"
                    :class="{ 'mb-2': index !== errors.length }">
                    <small><strong class="me-1">Errore:</strong>{{ error }}</small>
                  </div>
                </template>
              </template>

              <button class="w-100 btn btn-lg d-flex align-items-center justify-content-center btn-violet" type="submit"
                :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                <span v-if="loading">Loading...</span>
                <span v-else>{{ $t('Sign in') }}</span>
              </button>

            </form>

          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import { reactive, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { auth0Login as auth0LoginRepo } from "@/use/repositories/auth/auth0Login.js";
import { login } from "@/use/repositories/auth/login.js";
import { handleLogin } from "@/use/utilities/handleLogin.js";
import { departmentException } from "@/use/departmentException";
import Brand from "@/components/Brand.vue";
import {
  auth0Login as auth0Redirect,
  auth0HandleCallback,
  auth0GetToken,
} from "@/composables/useAuth0.js";

const isAuth0 = process.env.VUE_APP_AUTH0_ENABLED === "true";

export default {
  name: "Login",
  components: {
    Brand,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const i18n = useI18n();

    // Shared state.
    const error = ref(null);
    const loading = ref(false);

    // Legacy state.
    const errors = [];
    const data = reactive({
      email: "",
      password: "",
    });

    // Auth0 flow
    const redirectToAuth0 = async () => {
      error.value = null;
      await auth0Redirect();
    };

    const handleAuth0Mount = async () => {
      await store.dispatch("setUser", null);
      await store.dispatch("setMainRole", null);

      const params = new URLSearchParams(window.location.search);
      const hasCallback = params.has("code") && params.has("state");
      const hasError = params.has("error");

      if (hasError) {
        const errorDesc = params.get("error_description") || params.get("error");
        console.error("Auth0 redirect error:", errorDesc);
        error.value = errorDesc;
      } else if (hasCallback) {
        try {
          await auth0HandleCallback();
        } catch (e) {
          console.error("Auth0 handleRedirectCallback error:", e);
          error.value = e?.message || "Errore durante la gestione del callback Auth0.";
          return;
        }

        try {
          const accessToken = await auth0GetToken();
          const response = await auth0LoginRepo(accessToken);

          if (!response.data?.meta?.roles?.length) {
            throw new Error(i18n.t("Unauthorized"));
          }

          localStorage.setItem("authToken", response.data.meta.token);
          await store.dispatch("setUser", response.data.data);
          await store.dispatch("setUserData", response.data.meta.roles);
          await handleLogin();
        } catch (e) {
          console.error("Auth0 login error:", e);
          if (Array.isArray(e)) {
            error.value = e.join(", ");
          } else {
            error.value = e?.message || "Si è verificato un errore durante l'autenticazione.";
          }
        }
      } else {
        await redirectToAuth0();
      }
    };

    // Legacy flow
    const handleLegacyMount = async () => {
      await store.dispatch("setUser", null);
      await store.dispatch("setMainRole", null);
    };

    const submit = async () => {
      loading.value = true;
      errors.splice(0);

      login(data).then(async (response) => {
        if (!response.data?.meta?.roles?.length) {
          throw { response: { data: { errors: [i18n.t("Unauthorized")] } } };
        }

        localStorage.setItem("authToken", response.data.meta.token);
        loading.value = false;

        await store.dispatch("setUser", response.data.data);
        await store.dispatch("setUserData", response.data.meta.roles);

        if (!departmentException(response.data.meta.roles, true)) {
          if (response.data.data['2fa'] === null) {
            store.state.twoFactorAuth.enabled = true;
            store.state.twoFactorAuth.action = 'register';
          } else if (response.data.data['2fa'].google2fa_enable === 0) {
            store.state.twoFactorAuth.enabled = true;
            store.state.twoFactorAuth.action = 'confirm';
          } else if (response.data.data['2fa'].google2fa_enable === 1) {
            store.state.twoFactorAuth.enabled = true;
            store.state.twoFactorAuth.action = 'verify';
          }

          if (store.state.twoFactorAuth.enabled)
            return await router.push({
              name: "TwoFactorAuth",
              params: { type: store.state.twoFactorAuth.action }
            });
        }

        handleLogin();
      }).catch(async (error) => {
        let messages = error.response.data.errors;
        messages.forEach((message) => errors.push(message));
      }).finally(() => {
        loading.value = false;
      });
    };

    onMounted(async () => {
      if (isAuth0) {
        await handleAuth0Mount();
      } else {
        await handleLegacyMount();
      }
    });

    return {
      isAuth0,
      error,
      redirectToAuth0,
      data,
      submit,
      errors,
      loading,
    };
  },
};
</script>
